import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { ref, get, query, orderByChild, equalTo } from 'firebase/database';

// ──────────────────────────────────────────────────────────────
// PerformanceLab — stats reales de /signals_history
//
// 2026-05-10: alineado a "punto de vista del cliente VIP" para
// coincidir con sendDailyReport. REGLA DURA (ver DECISIONES.md):
//   - Ventana = día calendario hora Chile (NO rolling 24h)
//   - Dimensión temporal = started_at (cuándo se EMITIÓ el pick)
//   - Filtro = telegram_sent === true AND result IN ('WIN','LOSS')
//   - PUSH se descarta del denominador WR
//
// Periodos:
//   HOY    = [00:00 hora Chile hoy, 00:00 hora Chile mañana)
//   SEMANA = [hace 7 días 00:00, hoy 00:00)   ← 7 días completos sin hoy
//   MES    = [hace 30 días 00:00, hoy 00:00)
// ──────────────────────────────────────────────────────────────

const PERIODS = {
  HOY:    { label: 'Hoy',     daysBack: 0 },
  SEMANA: { label: '7 días',  daysBack: 7 },
  MES:    { label: '30 días', daysBack: 30 },
};

// ── Resolución día Chile (DST-safe) ───────────────────────────
// Igual lógica que functions/dailyReport.js: probe UTC-3/UTC-4
// contra Intl.DateTimeFormat con timeZone Santiago. Funciona tanto
// en horario verano como invierno.
export function santiagoDateStr(date) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date);
}

export function santiagoMidnightToUTCms(yyyyMmDd) {
  const y = parseInt(yyyyMmDd.slice(0, 4), 10);
  const m = parseInt(yyyyMmDd.slice(5, 7), 10) - 1;
  const d = parseInt(yyyyMmDd.slice(8, 10), 10);
  for (const utcHour of [3, 4]) {
    const candidate = Date.UTC(y, m, d, utcHour, 0, 0);
    const hhmm = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Santiago',
      hour12: false, hour: '2-digit', minute: '2-digit',
    }).format(new Date(candidate));
    if (hhmm === '00:00' || hhmm === '24:00') return candidate;
  }
  return Date.UTC(y, m, d, 4, 0, 0); // fallback UTC-4
}

export function shiftDays(yyyyMmDd, deltaDays) {
  const anchor = new Date(`${yyyyMmDd}T12:00:00Z`);
  anchor.setUTCDate(anchor.getUTCDate() + deltaDays);
  return santiagoDateStr(anchor);
}

// Ventana del período en ms UTC + labels human readable hora Chile.
function getWindow(period, now) {
  const todayStr = santiagoDateStr(now);
  const tomorrowStr = shiftDays(todayStr, 1);
  if (period === 'HOY') {
    return {
      startMs: santiagoMidnightToUTCms(todayStr),
      endMs:   santiagoMidnightToUTCms(tomorrowStr),
      label:   `Hoy · ${formatDayChile(todayStr)} (00:00 → 23:59:59 hora Chile)`,
    };
  }
  const back = PERIODS[period].daysBack;
  const startStr = shiftDays(todayStr, -back);
  const endStr = todayStr; // exclusivo (= 00:00 hoy)
  return {
    startMs: santiagoMidnightToUTCms(startStr),
    endMs:   santiagoMidnightToUTCms(endStr),
    label:   `${PERIODS[period].label} · ${formatDayChile(startStr)} → ${formatDayChile(shiftDays(endStr, -1))} (hora Chile)`,
  };
}

function formatDayChile(yyyyMmDd) {
  const dd = yyyyMmDd.slice(8, 10);
  const mm = yyyyMmDd.slice(5, 7);
  const yyyy = yyyyMmDd.slice(0, 4);
  return `${dd}/${mm}/${yyyy}`;
}

function formatRelativeTime(ts, now) {
  if (!ts) return '—';
  const diffMs = now - ts;
  if (diffMs < 0) return 'ahora';
  if (diffMs < 60_000) return `hace ${Math.floor(diffMs / 1000)}s`;
  if (diffMs < 3_600_000) return `hace ${Math.floor(diffMs / 60_000)} min`;
  if (diffMs < 86_400_000) return `hace ${Math.floor(diffMs / 3_600_000)}h`;
  return new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).format(new Date(ts));
}

// ── Helpers ───────────────────────────────────────────────────
function formatDuration(secs) {
  if (!secs || secs < 0) return '—';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs) % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

function formatPct(v) {
  if (v == null || isNaN(v)) return '—';
  return `${v.toFixed(1)}%`;
}

function pctRate(wins, decided) {
  return decided > 0 ? (wins / decided) * 100 : null;
}

export function statsOfSubset(subset) {
  const total  = subset.length;
  const wins   = subset.filter(s => s.result === 'WIN').length;
  const losses = subset.filter(s => s.result === 'LOSS').length;
  const pushes = subset.filter(s => s.result === 'PUSH').length;
  return {
    total, wins, losses, pushes,
    decided: wins + losses,
    winRate: pctRate(wins, wins + losses),
  };
}

// ── Componente principal ──────────────────────────────────────
export default function PerformanceLab() {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod]   = useState('HOY');
  // Timestamp del último snapshot RTDB recibido (para "Última actualización")
  const [lastDataTs, setLastDataTs] = useState(null);
  // "now" del browser usado para construir ventanas. Bumpable via botón refresh
  // para forzar re-cálculo de labels y de ventana HOY (parcial hasta now).
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    // Solo señales reales (telegram_sent === true) y una sola lectura (get):
    // es una vista de stats sobre historial cerrado, no necesita realtime.
    const q = query(ref(db, 'signals_history'), orderByChild('telegram_sent'), equalTo(true));
    let cancelled = false;
    get(q)
      .then(snap => {
        if (cancelled) return;
        const data = snap.val() || {};
        // 2026-09-04 (OK Matías): KPIs de básquet — las señales handball
        // (emisor EGH1, sport/match_type='handball') NO cuentan acá.
        const list = Object.entries(data)
          .map(([id, val]) => ({ id, ...val }))
          .filter(s => s.match_type !== 'handball' && s.sport !== 'handball');
        setSignals(list);
        setLastDataTs(Date.now());
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('[PerformanceLab] Firebase error:', err);
        setSignals([]);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Auto-tick para refrescar "hace N min" cada 10s sin requerir click manual.
  // 10s vs 30s da más sensación de "datos vivos" sin sobrecargar (es solo
  // recomputar ventana + labels, no fetch).
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 10_000);
    return () => clearInterval(id);
  }, []);

  // Ventana del período en ms UTC. Recalcula cuando cambia period o nowTick.
  const window_ = useMemo(() => getWindow(period, new Date(nowTick)), [period, nowTick]);

  // Filtro alineado a sendDailyReport — punto de vista del cliente VIP:
  //   started_at en ventana + telegram_sent === true + result IN (WIN, LOSS)
  // PUSH se descarta del denominador WR pero NO contamos pushes acá (no entran).
  const closed = useMemo(() => {
    return signals.filter(s =>
      s.telegram_sent === true &&
      typeof s.started_at === 'number' &&
      s.started_at >= window_.startMs &&
      s.started_at < window_.endMs &&
      (s.result === 'WIN' || s.result === 'LOSS')
    );
  }, [signals, window_]);

  const general = useMemo(() => {
    const s = statsOfSubset(closed);
    const avgEdgeAbs = closed.length > 0
      ? closed.reduce((acc, x) => acc + Math.abs(x.edge_peak || x.edge_initial || 0), 0) / closed.length
      : 0;
    const avgDuration = closed.length > 0
      ? closed.reduce((acc, x) => acc + (x.duration_secs || 0), 0) / closed.length
      : 0;
    return { ...s, avgEdgeAbs, avgDuration };
  }, [closed]);

  const byType = useMemo(() => ({
    under: statsOfSubset(closed.filter(s => s.signal_type === 'UNDER_FUERTE')),
    over:  statsOfSubset(closed.filter(s => s.signal_type === 'OVER_FUERTE')),
  }), [closed]);

  const topLigas = useMemo(() => {
    const map = {};
    for (const s of closed) {
      const lg = s.liga || 'Sin liga';
      if (!map[lg]) map[lg] = { wins: 0, losses: 0, pushes: 0, total: 0 };
      map[lg].total++;
      if (s.result === 'WIN')       map[lg].wins++;
      else if (s.result === 'LOSS') map[lg].losses++;
      else if (s.result === 'PUSH') map[lg].pushes++;
    }
    return Object.entries(map)
      .map(([name, v]) => ({
        name,
        total: v.total,
        wins: v.wins,
        losses: v.losses,
        pushes: v.pushes,
        decided: v.wins + v.losses,
        winRate: pctRate(v.wins, v.wins + v.losses),
      }))
      .filter(lg => lg.decided >= 3)
      .sort((a, b) => (b.winRate ?? 0) - (a.winRate ?? 0) || b.total - a.total)
      .slice(0, 5);
  }, [closed]);

  // ── Series derivadas para Sparklines + Wins netos + Recientes ──
  // Todas operan sobre `closed` ordenado cronológicamente (asc por started_at,
  // que es la dimensión temporal del cliente VIP).
  const series = useMemo(() => {
    const sortedAsc = [...closed].sort(
      (a, b) => (a.started_at || 0) - (b.started_at || 0)
    );

    // 1) Wins acumulados — cumsum chronológico
    const winsAccum = [];
    let acc = 0;
    for (const s of sortedAsc) {
      if (s.result === 'WIN') acc += 1;
      winsAccum.push(acc);
    }

    // 2) Win rate móvil — rolling sobre últimas N decididas
    //    N = 10 si hay <30 cerradas, N = 20 si >=30
    const decidedAsc = sortedAsc.filter(
      s => s.result === 'WIN' || s.result === 'LOSS'
    );
    const N = decidedAsc.length >= 30 ? 20 : 10;
    const winRateRolling = [];
    for (let i = 0; i < decidedAsc.length; i++) {
      const lo = Math.max(0, i - N + 1);
      const window = decidedAsc.slice(lo, i + 1);
      const w = window.filter(s => s.result === 'WIN').length;
      winRateRolling.push((w / window.length) * 100);
    }

    // 3) Edge peak promedio móvil — rolling de |edge_peak|
    const edgePeakRolling = [];
    for (let i = 0; i < sortedAsc.length; i++) {
      const lo = Math.max(0, i - N + 1);
      const window = sortedAsc.slice(lo, i + 1);
      const sum = window.reduce(
        (a, x) => a + Math.abs(x.edge_peak || x.edge_initial || 0),
        0
      );
      edgePeakRolling.push(sum / window.length);
    }

    // 4) Wins netos acumulados — cumsum(W) − cumsum(L), pushes ignorados
    const winsNetos = [];
    let net = 0;
    for (const s of sortedAsc) {
      if (s.result === 'WIN') net += 1;
      else if (s.result === 'LOSS') net -= 1;
      winsNetos.push({ ts: s.started_at, value: net });
    }

    // 5) Últimas 10 — orden descendente por started_at
    const recientes = [...closed]
      .sort((a, b) => (b.started_at || 0) - (a.started_at || 0))
      .slice(0, 10);

    return {
      winsAccum,
      winRateRolling,
      edgePeakRolling,
      winsNetos,
      recientes,
      rollingWindowSize: N,
      decidedCount: decidedAsc.length,
    };
  }, [closed]);

  if (loading) {
    return (
      <div className="page" style={{ padding: 20 }}>
        <div style={{ color: 'var(--muted)', textAlign: 'center', padding: 48 }}>
          Cargando Performance Lab…
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ padding: '24px 28px' }}>
      <Header
        period={period}
        setPeriod={setPeriod}
        total={general.total}
        windowLabel={window_.label}
        lastDataTs={lastDataTs}
        nowTick={nowTick}
        onRefresh={() => setNowTick(Date.now())}
      />

      {general.total === 0 ? (
        <EmptyMsg windowLabel={window_.label} />
      ) : (
        <>
          <KpisRow general={general} />
          <ByType under={byType.under} over={byType.over} />
          <TopLigas ligas={topLigas} />
          <SparkCardsRow general={general} series={series} period={period} />
          <WinsNetosSection winsNetos={series.winsNetos} />
          <RecentSignals recientes={series.recientes} />
        </>
      )}
    </div>
  );
}

// ── Header con título y filtros ───────────────────────────────
function Header({ period, setPeriod, total, windowLabel, lastDataTs, nowTick, onRefresh }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h1 className="page-title" style={{ margin: 0 }}>
        Performance Lab
      </h1>
      <div className="page-sub" style={{ marginTop: 4, fontSize: 13, color: 'var(--muted)' }}>
        Punto de vista del cliente VIP · {total} picks emitidos
      </div>
      <div style={{
        display: 'flex',
        gap: 8,
        marginTop: 14,
        overflowX: 'auto',
        paddingBottom: 4,
        WebkitOverflowScrolling: 'touch',
      }}>
        {Object.keys(PERIODS).map(key => (
          <FilterTab
            key={key}
            active={period === key}
            onClick={() => setPeriod(key)}
            label={PERIODS[key].label}
          />
        ))}
      </div>

      {/* Ventana exacta + última actualización + refresh */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        marginTop: 14,
        padding: '10px 12px',
        background: 'var(--bg-elev)',
        border: '0.5px solid var(--border)',
        borderRadius: 10,
      }}>
        <div style={{
          fontSize: 12,
          color: 'var(--ink)',
          fontWeight: 500,
          minWidth: 0,
          flex: 1,
        }}>
          <span style={{ marginRight: 6 }}>📅</span>
          <span style={{ color: 'var(--muted)', marginRight: 6 }}>Ventana:</span>
          {windowLabel}
        </div>
        <div style={{
          fontSize: 11,
          color: 'var(--muted-2, var(--muted))',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          whiteSpace: 'nowrap',
        }}>
          <span>🕐 Actualizado {formatRelativeTime(lastDataTs, nowTick)}</span>
          <button
            onClick={onRefresh}
            style={{
              background: 'transparent',
              border: '0.5px solid var(--border)',
              color: 'var(--ink)',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 11,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
            title="Re-calcular ventana con la hora actual"
          >
            🔄 Actualizar
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterTab({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      background: active ? 'var(--ink)' : 'transparent',
      color: active ? 'var(--bg-elev)' : 'var(--ink-2)',
      border: '0.5px solid ' + (active ? 'var(--ink)' : 'var(--border)'),
      padding: '6px 14px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 500,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      fontFamily: 'inherit',
    }}>
      {label}
    </button>
  );
}

// ── Mensaje vacío ─────────────────────────────────────────────
function EmptyMsg({ windowLabel }) {
  return (
    <div style={{
      padding: 40,
      textAlign: 'center',
      background: 'var(--bg-elev)',
      border: '0.5px solid var(--border)',
      borderRadius: 12,
    }}>
      <div style={{
        fontSize: 16,
        color: 'var(--ink)',
        fontWeight: 500,
        marginBottom: 6,
      }}>
        Sin picks emitidos a VIP en este período
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)', maxWidth: 360, margin: '0 auto', lineHeight: 1.5 }}>
        {windowLabel}
        <br />
        Cuando el motor emita picks al VIP y se resuelvan WIN/LOSS,
        van a aparecer acá automáticamente.
      </div>
    </div>
  );
}

// ── KPIs principales ──────────────────────────────────────────
function KpisRow({ general }) {
  const { total, wins, losses, pushes, winRate, decided, avgEdgeAbs, avgDuration } = general;
  const pushSub = pushes > 0 ? ` · ${pushes} PUSH` : '';
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
      gap: 12,
      marginBottom: 22,
    }}>
      <Kpi
        label="Total"
        value={total}
        sub={`${decided} decididas${pushSub}`}
        tone="neutral"
      />
      <Kpi
        label="Ganadas"
        value={wins}
        sub={`Edge prom +${avgEdgeAbs.toFixed(1)}`}
        tone="win"
      />
      <Kpi
        label="Perdidas"
        value={losses}
        sub={`Duración prom ${formatDuration(avgDuration)}`}
        tone="loss"
      />
      <Kpi
        label="Win rate"
        value={formatPct(winRate)}
        sub={decided > 0 ? `${wins}W / ${losses}L` : '—'}
        tone={winRate >= 50 ? 'win' : 'loss'}
        isText
      />
    </div>
  );
}

function Kpi({ label, value, sub, tone, isText }) {
  const tones = {
    neutral: { bg: 'var(--bg-elev)',  fg: 'var(--ink)', subColor: 'var(--muted)', border: '0.5px solid var(--border)' },
    win:     { bg: 'var(--pos-soft)', fg: 'var(--pos)', subColor: 'var(--pos)',   border: 'none' },
    loss:    { bg: 'var(--neg-soft)', fg: 'var(--neg)', subColor: 'var(--neg)',   border: 'none' },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <div style={{
      background: t.bg,
      padding: '14px 16px',
      borderRadius: 10,
      border: t.border,
    }}>
      <div style={{
        fontSize: 11,
        color: t.subColor,
        marginBottom: 6,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}>{label}</div>
      <div style={{
        fontSize: isText ? 22 : 28,
        fontWeight: 600,
        color: t.fg,
        lineHeight: 1.1,
      }}>{value}</div>
      {sub && (
        <div style={{ fontSize: 11, color: t.subColor, marginTop: 6 }}>{sub}</div>
      )}
    </div>
  );
}

// ── Por tipo (UNDER vs OVER) ──────────────────────────────────
function ByType({ under, over }) {
  return (
    <Section title="Por tipo de señal">
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 12,
      }}>
        <TypeCard
          label="UNDER ★"
          stats={under}
          color="var(--pos)"
          bg="var(--pos-soft)"
        />
        <TypeCard
          label="OVER ★"
          stats={over}
          color="var(--warn)"
          bg="var(--warn-soft)"
        />
      </div>
    </Section>
  );
}

function TypeCard({ label, stats, color, bg }) {
  return (
    <div style={{
      background: bg,
      padding: '14px 16px',
      borderRadius: 10,
      borderLeft: `3px solid ${color}`,
    }}>
      <div style={{
        fontSize: 11,
        color,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: 8,
      }}>{label}</div>
      <div style={{
        fontSize: 24,
        fontWeight: 600,
        color: 'var(--ink)',
        lineHeight: 1,
      }}>
        {formatPct(stats.winRate)}
      </div>
      <div style={{ fontSize: 11, color: 'var(--muted-2)', marginTop: 6 }}>
        {stats.total} señales · {stats.wins}W · {stats.losses}L
        {stats.pushes > 0 && ` · ${stats.pushes}P`}
      </div>
    </div>
  );
}

// ── Sección Telegram vs Sin Telegram removida (2026-05-10)
// El refactor a "punto de vista del cliente VIP" filtra
// telegram_sent === true por construcción en `closed`, así que
// la comparación TG vs Sin-TG ya no aporta información: el panel
// SIEMPRE es 100% TG. Auditoría motor (incluye no-enviados) queda
// para vista separada futura.

// ── Top 5 ligas ───────────────────────────────────────────────
function TopLigas({ ligas }) {
  return (
    <Section title="Top 5 ligas" sub="Ordenadas por win rate (desempate por volumen)">
      {ligas.length === 0 ? (
        <div style={{
          padding: 24,
          fontSize: 12,
          color: 'var(--muted)',
          textAlign: 'center',
          background: 'var(--bg-elev)',
          border: '0.5px solid var(--border)',
          borderRadius: 10,
        }}>
          Sin señales por liga aún
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-elev)',
          border: '0.5px solid var(--border)',
          borderRadius: 10,
          overflow: 'hidden',
        }}>
          {ligas.map((lg, i) => (
            <LigaRow
              key={lg.name}
              idx={i + 1}
              liga={lg}
              isLast={i === ligas.length - 1}
            />
          ))}
        </div>
      )}
    </Section>
  );
}

function LigaRow({ idx, liga, isLast }) {
  const positive = liga.winRate >= 50;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '24px 1fr auto auto',
      gap: 10,
      alignItems: 'center',
      padding: '12px 14px',
      borderBottom: isLast ? 'none' : '0.5px solid var(--border)',
    }}>
      <div style={{ fontSize: 10, color: 'var(--muted-2)', fontWeight: 600 }}>
        {String(idx).padStart(2, '0')}
      </div>
      <div style={{
        fontSize: 13,
        fontWeight: 500,
        color: 'var(--ink)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
      }}>
        {liga.name}
      </div>
      <div style={{
        fontSize: 11,
        color: 'var(--muted)',
        whiteSpace: 'nowrap',
      }}>
        {liga.total} · {liga.wins}W
      </div>
      <div style={{
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: positive ? 'var(--pos)' : 'var(--neg)',
        minWidth: 56,
        textAlign: 'right',
      }}>
        {formatPct(liga.winRate)}
      </div>
    </div>
  );
}

// ── Wrapper de sección ────────────────────────────────────────
function Section({ title, sub, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
          {title}
        </div>
        {sub && (
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
            {sub}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SECCIÓN 5 — Sparklines (3 tarjetas)
// ══════════════════════════════════════════════════════════════
function SparkCardsRow({ general, series, period }) {
  const { winsAccum, winRateRolling, edgePeakRolling, rollingWindowSize } = series;

  // No tiene sentido renderizar sparklines con < 2 puntos
  if (general.total < 2) return null;

  const lastWinRate = winRateRolling.length > 0
    ? winRateRolling[winRateRolling.length - 1]
    : null;
  const lastEdge = edgePeakRolling.length > 0
    ? edgePeakRolling[edgePeakRolling.length - 1]
    : 0;
  const winRateColor = lastWinRate == null
    ? 'var(--muted)'
    : lastWinRate > 50
      ? 'var(--pos)'
      : 'var(--neg)';

  return (
    <Section title="Tendencias" sub={`Evolución durante el período seleccionado (móvil ${rollingWindowSize})`}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12,
      }}>
        <SparkCard
          label="Wins acumulados"
          value={String(general.wins)}
          footer={`${general.wins} wins · período actual`}
          vals={winsAccum}
          color="var(--pos)"
        />
        <SparkCard
          label="Win rate móvil"
          value={lastWinRate == null ? '—' : `${lastWinRate.toFixed(1)}%`}
          footer={lastWinRate == null
            ? 'Sin decididas suficientes'
            : `${lastWinRate.toFixed(1)}% en últimas ${rollingWindowSize}`}
          vals={winRateRolling}
          color={winRateColor}
        />
        <SparkCard
          label="Edge peak prom"
          value={`+${lastEdge.toFixed(1)}`}
          footer={`+${lastEdge.toFixed(1)} promedio últimas ${rollingWindowSize}`}
          vals={edgePeakRolling}
          color="var(--warn)"
        />
      </div>
    </Section>
  );
}

function SparkCard({ label, value, footer, vals, color }) {
  return (
    <div style={{
      background: 'var(--bg-elev)',
      border: '0.5px solid var(--border)',
      borderRadius: 10,
      padding: '14px 16px',
    }}>
      <div style={{
        fontSize: 11,
        color: 'var(--muted)',
        marginBottom: 6,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}>{label}</div>
      <div style={{
        fontSize: 22,
        fontWeight: 600,
        color: 'var(--ink)',
        lineHeight: 1,
      }}>{value}</div>
      <Sparkline vals={vals} color={color} />
      <div style={{ fontSize: 11, color: 'var(--muted-2)', marginTop: 4 }}>
        {footer}
      </div>
    </div>
  );
}

function Sparkline({ vals, color }) {
  // Necesita al menos 2 puntos para dibujar línea
  const safe = (vals && vals.length >= 2) ? vals : [0, 0];
  const max = Math.max(...safe);
  const min = Math.min(...safe);
  const range = max - min || 1;
  const W = 100;
  const H = 32;

  const points = safe.map((v, i) => {
    const x = (i / (safe.length - 1)) * W;
    const y = H - ((v - min) / range) * (H - 6) - 3;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  const pathLine = points.join(' ');
  const last = points[points.length - 1].split(',');
  // Sanitize gradId: works for hex, rgb(), or var(--xxx) inputs
  const gradId = `sk-grad-${color.replace(/[^A-Za-z0-9]/g, '')}`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: 36, marginTop: 8 }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d={`M 0,${H} L ${pathLine.replace(/,/g, ' ').replace(/ (?=\d)/g, ' L ').replace('L L', 'L')} L ${W},${H} Z`}
        fill={`url(#${gradId})`}
      />
      <polyline
        points={pathLine}
        fill="none"
        stroke={color}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={last[0]} cy={last[1]} r="2" fill={color} />
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════
// SECCIÓN 6 — Wins netos acumulados (gráfico amplio)
// ══════════════════════════════════════════════════════════════
function WinsNetosSection({ winsNetos }) {
  if (winsNetos.length < 2) {
    return (
      <Section
        title="Wins netos acumulados"
        sub="Evolución de wins menos losses, ordenado por cierre"
      >
        <div style={{
          padding: 24,
          textAlign: 'center',
          background: 'var(--bg-elev)',
          border: '0.5px solid var(--border)',
          borderRadius: 10,
          fontSize: 12,
          color: 'var(--muted)',
        }}>
          Necesitás al menos 2 señales decididas para ver evolución.
        </div>
      </Section>
    );
  }

  const lastValue = winsNetos[winsNetos.length - 1].value;
  const positivo = lastValue >= 0;

  return (
    <Section
      title="Wins netos acumulados"
      sub={`${positivo ? '+' : ''}${lastValue} netos al final del período`}
    >
      <div style={{
        background: 'var(--bg-elev)',
        border: '0.5px solid var(--border)',
        borderRadius: 10,
        padding: '18px 16px 14px',
      }}>
        <WinsNetosChart winsNetos={winsNetos} positivo={positivo} />
      </div>
    </Section>
  );
}

function WinsNetosChart({ winsNetos, positivo }) {
  const W = 100;
  const H = 50;
  const values = winsNetos.map(p => p.value);
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const range = (max - min) || 1;

  // y(value) en coords SVG (top=0). Pad 4 arriba/abajo.
  const yOf = (v) => H - 4 - ((v - min) / range) * (H - 8);

  const points = winsNetos.map((p, i) => {
    const x = (i / (winsNetos.length - 1)) * W;
    return `${x.toFixed(2)},${yOf(p.value).toFixed(2)}`;
  });
  const pathLine = points.join(' ');
  const lastP = points[points.length - 1].split(',');
  const yZero = yOf(0);
  const lastValue = values[values.length - 1];

  const color = positivo ? 'var(--pos)' : 'var(--neg)';

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMinYMid meet"
      style={{ width: '100%', height: 180, display: 'block' }}
    >
      <defs>
        <linearGradient id="winsnetos-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.20" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Grid sutil cada 25% */}
      {[0.25, 0.5, 0.75].map(g => (
        <line
          key={g}
          x1="0" x2={W}
          y1={H * g} y2={H * g}
          stroke="var(--border, rgba(212,211,200,.5))"
          strokeWidth="0.3"
          strokeDasharray="0.5 0.5"
          vectorEffect="non-scaling-stroke"
        />
      ))}

      {/* Línea de break-even (y = 0) */}
      <line
        x1="0" x2={W}
        y1={yZero} y2={yZero}
        stroke="var(--muted)"
        strokeWidth="0.5"
        strokeDasharray="1 1"
        opacity="0.6"
        vectorEffect="non-scaling-stroke"
      />

      {/* Área bajo la curva */}
      <path
        d={`M 0,${yZero} L ${pathLine.split(' ').map(p => p.replace(',', ' ')).join(' L ')} L ${W},${yZero} Z`}
        fill="url(#winsnetos-grad)"
      />

      {/* Línea principal */}
      <polyline
        points={pathLine}
        fill="none"
        stroke={color}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />

      {/* Punto final destacado */}
      <circle cx={lastP[0]} cy={lastP[1]} r="2" fill={color} />
      <circle cx={lastP[0]} cy={lastP[1]} r="3.5" fill={color} opacity="0.25" />

      {/* Label numérico junto al punto final */}
      <text
        x={Math.min(parseFloat(lastP[0]) + 2, W - 8)}
        y={Math.max(parseFloat(lastP[1]) - 1.5, 4)}
        fontSize="3.5"
        fill={color}
        fontWeight="600"
        style={{ paintOrder: 'stroke', stroke: 'var(--bg-elev, #fff)', strokeWidth: 0.8 }}
      >
        {lastValue >= 0 ? '+' : ''}{lastValue}
      </text>
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════
// SECCIÓN 7 — Señales recientes (últimas 10)
// ══════════════════════════════════════════════════════════════
function RecentSignals({ recientes }) {
  if (!recientes || recientes.length === 0) return null;

  return (
    <Section
      title="Señales recientes"
      sub={`Últimas ${recientes.length} cerradas (más recientes primero)`}
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}>
        {recientes.map(s => (
          <RecentRow key={s.id} signal={s} />
        ))}
      </div>
    </Section>
  );
}

function RecentRow({ signal }) {
  const isUnder = signal.signal_type === 'UNDER_FUERTE';
  const isWin   = signal.result === 'WIN';
  const isLoss  = signal.result === 'LOSS';
  const isPush  = signal.result === 'PUSH';

  const sideColor = isUnder ? 'var(--pos)' : 'var(--warn)';
  const sideLabel = isUnder ? 'UNDER ★' : 'OVER ★';

  // Hora HH:MM hora Chile de cuándo el pick salió al VIP (started_at)
  const hora = signal.started_at
    ? new Date(signal.started_at).toLocaleTimeString('es-CL', {
        timeZone: 'America/Santiago',
        hour: '2-digit', minute: '2-digit', hour12: false,
      })
    : '—';

  // Truncado del partido
  const partido = signal.partido && signal.partido.length > 30
    ? signal.partido.slice(0, 28) + '…'
    : (signal.partido || '—');

  // Línea -> total
  const lineStr = signal.line != null ? String(signal.line) : '—';
  const totalStr = signal.total_final != null ? String(signal.total_final) : '—';

  // Diferencia signed
  const dif = signal.diferencia;
  const difStr = (dif != null && !isNaN(dif))
    ? `${dif >= 0 ? '+' : ''}${Number(dif).toFixed(1)}`
    : '—';

  // Duración
  const durStr = formatDuration(signal.duration_secs);

  // Result chip
  const chipBg = isWin  ? 'var(--pos-soft)'
               : isLoss ? 'var(--bg-hover)'
               : isPush ? 'var(--bg-hover)'
               : 'transparent';
  const chipFg = isWin  ? 'var(--pos)'
               : isLoss ? 'var(--muted)'
               : isPush ? 'var(--muted)'
               : 'var(--muted)';
  const chipText = isWin ? 'W ✓' : isLoss ? 'L ✗' : isPush ? 'PUSH' : signal.result || '—';

  // Opacidad sutil para LOSS (consistente con SignalsHistory)
  const rowOpacity = isLoss ? 0.7 : 1;

  return (
    <div style={{
      background: 'var(--bg-elev)',
      border: '0.5px solid var(--border)',
      borderLeft: `3px solid ${sideColor}`,
      borderRadius: 10,
      padding: '10px 12px',
      opacity: rowOpacity,
    }}>
      {/* Fila 1: hora + partido + chip */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 8,
        marginBottom: 6,
      }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0, flex: 1 }}>
          <span style={{
            fontSize: 11,
            color: 'var(--muted)',
            fontFamily: 'ui-monospace, monospace',
            flexShrink: 0,
          }}>{hora}</span>
          <span style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--ink)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}>{partido}</span>
        </div>
        <span style={{
          background: chipBg,
          color: chipFg,
          fontSize: 11,
          fontWeight: 700,
          padding: '3px 9px',
          borderRadius: 999,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          letterSpacing: '0.03em',
        }}>{chipText}</span>
      </div>

      {/* Fila 2: side + line→total (dif) + duración */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        gap: '4px 10px',
        fontSize: 12,
      }}>
        <span style={{
          color: sideColor,
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '0.04em',
        }}>{sideLabel}</span>
        <span style={{
          fontFamily: 'ui-monospace, monospace',
          color: 'var(--ink-2, var(--ink))',
        }}>
          {lineStr} → {totalStr}
        </span>
        {difStr !== '—' && (
          <span style={{
            color: isWin ? 'var(--pos)' : 'var(--muted)',
            fontFamily: 'ui-monospace, monospace',
            fontWeight: 600,
          }}>({difStr})</span>
        )}
        <span style={{
          color: 'var(--muted)',
          marginLeft: 'auto',
          flexShrink: 0,
        }}>
          {durStr}
        </span>
      </div>
    </div>
  );
}
